import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot, faCircle, faPlay, faStop, faDownload,
  faExclamationTriangle, faCheckCircle, faSpinner, faCog,
  faHistory, faChevronDown, faChevronRight, faShieldAlt,
  faSearch, faTimes, faCheckSquare, faSquare
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import exposiqService from '../../services/exposiqService';
import authProfileService from '../../services/authProfileService';
import httpExchangeService from '../../services/httpExchangeService';
import ProfileSelect from './ProfileSelect';
import ExchangePanel from './ExchangePanel';

const METHOD_COLORS = {
  GET: 'text-green-400', POST: 'text-amber-400', PUT: 'text-blue-400',
  PATCH: 'text-purple-400', DELETE: 'text-red-400'
};
const epKey = (e) => `${e.method || 'GET'} ${e.url}`;

// Derive the web target's primary host from its base URL.
const primaryHost = (webTarget) => {
  try { return new URL(webTarget?.baseUrl).hostname; } catch { return webTarget?.baseUrl || ''; }
};

const SEVERITY_COLORS = {
  critical: 'bg-red-600',
  high: 'bg-red-400',
  medium: 'bg-yellow-500',
  low: 'bg-blue-400',
  informational: 'bg-gray-400',
  info: 'bg-gray-400'
};

const ExposIQScanTab = ({ webTarget, isDarkMode, onRefresh }) => {
  const { t } = useLanguage();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanProfile, setScanProfile] = useState('standard');
  const [authProfiles, setAuthProfiles] = useState([]);
  const [dastEnabled, setDastEnabled] = useState(false);
  const [dastProfileId, setDastProfileId] = useState('');
  // DAST endpoint selection
  const [dastEndpoints, setDastEndpoints] = useState([]);
  const [dastSelected, setDastSelected] = useState(() => new Set()); // set of epKey
  const [dastLoadingEndpoints, setDastLoadingEndpoints] = useState(false);
  const [dastSearch, setDastSearch] = useState('');
  const [dastMethodFilter, setDastMethodFilter] = useState('ALL');
  const [dastAuthHeaders, setDastAuthHeaders] = useState([]);
  // Request/response detail modal
  const [detailExchange, setDetailExchange] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Host-level scan (no specific URLs): scan one or several hosts.
  const [discoveredHosts, setDiscoveredHosts] = useState([]);
  const [selectedHosts, setSelectedHosts] = useState([]);
  const [customHost, setCustomHost] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef(null);

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';

  const checkConnection = useCallback(async () => {
    try {
      setLoading(true);
      const status = await exposiqService.getConnectionStatus(webTarget?._id);
      setConnectionStatus(status);
    } catch {
      setConnectionStatus({ connected: false, error: 'Failed to check connection' });
    } finally {
      setLoading(false);
    }
  }, [webTarget?._id]);

  const loadHistory = useCallback(async () => {
    if (!webTarget?._id) return;
    try {
      const data = await exposiqService.getScanHistory(webTarget._id);
      setScanHistory(data || []);
    } catch {
      setScanHistory([]);
    }
  }, [webTarget?._id]);

  useEffect(() => {
    checkConnection();
    loadHistory();
    // Resume polling if there's an active scan
    if (webTarget?.activeScan?.exposiqScanId &&
        ['running', 'queued'].includes(webTarget.activeScan.status)) {
      setScanning(true);
      setScanStatus({
        scanId: webTarget.activeScan.exposiqScanId,
        status: webTarget.activeScan.status,
        progress: webTarget.activeScan.progress,
        findingsCount: webTarget.activeScan.findingsCount
      });
      startPollingScanStatus(webTarget.activeScan.exposiqScanId);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [checkConnection, loadHistory, webTarget]);

  const startPollingScanStatus = (exposiqScanId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await exposiqService.pollScanStatus(exposiqScanId);
        setScanStatus(status);
        if (['completed', 'failed', 'stopped', 'cancelled'].includes(status.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setScanning(false);
          loadHistory();
          if (onRefresh) onRefresh();
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setScanning(false);
      }
    }, 3000);
  };

  // Load auth profiles for the authenticated-DAST picker.
  useEffect(() => {
    if (webTarget?.project) {
      authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
    }
  }, [webTarget?.project]);

  // Discover hosts seen in captured traffic; pre-select the target's primary host.
  useEffect(() => {
    if (!webTarget?._id) return;
    const main = primaryHost(webTarget);
    setSelectedHosts(main ? [main] : []);
    httpExchangeService.getSiteTree(webTarget._id)
      .then(data => {
        const hosts = [...new Set((data?.tree || []).map(n => n.host).filter(Boolean))].sort();
        // Ensure the primary host is always offered even if it has no captured traffic yet.
        if (main && !hosts.includes(main)) hosts.unshift(main);
        setDiscoveredHosts(hosts);
      })
      .catch(() => setDiscoveredHosts(main ? [main] : []));
  }, [webTarget]);

  const toggleHost = (host) => {
    setSelectedHosts(prev => prev.includes(host) ? prev.filter(h => h !== host) : [...prev, host]);
  };

  const addCustomHost = () => {
    const h = customHost.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
    if (!h) return;
    if (!discoveredHosts.includes(h)) setDiscoveredHosts(prev => [...prev, h]);
    if (!selectedHosts.includes(h)) setSelectedHosts(prev => [...prev, h]);
    setCustomHost('');
  };

  // Load the candidate DAST endpoints whenever a profile is chosen (all initially checked).
  useEffect(() => {
    if (!dastEnabled || !dastProfileId || !webTarget?._id) {
      setDastEndpoints([]); setDastSelected(new Set()); setDastAuthHeaders([]);
      return;
    }
    let cancelled = false;
    setDastLoadingEndpoints(true);
    exposiqService.getDastEndpoints(webTarget._id, dastProfileId)
      .then(data => {
        if (cancelled) return;
        const eps = data?.endpoints || [];
        setDastEndpoints(eps);
        setDastSelected(new Set(eps.map(epKey))); // all checked initially
        setDastAuthHeaders(data?.authHeaders || []);
      })
      .catch(() => { if (!cancelled) { setDastEndpoints([]); setDastSelected(new Set()); } })
      .finally(() => { if (!cancelled) setDastLoadingEndpoints(false); });
    return () => { cancelled = true; };
  }, [dastEnabled, dastProfileId, webTarget?._id]);

  const dastMethods = ['ALL', ...Array.from(new Set(dastEndpoints.map(e => e.method || 'GET'))).sort()];
  const filteredDastEndpoints = dastEndpoints.filter(e => {
    if (dastMethodFilter !== 'ALL' && (e.method || 'GET') !== dastMethodFilter) return false;
    if (dastSearch) {
      const q = dastSearch.toLowerCase();
      if (!e.url.toLowerCase().includes(q) && !(e.method || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const toggleEndpoint = (e) => {
    const k = epKey(e);
    setDastSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };
  const selectAllDast = () => setDastSelected(new Set(dastEndpoints.map(epKey)));
  const deselectAllDast = () => setDastSelected(new Set());
  const selectFilteredDast = () => setDastSelected(prev => { const n = new Set(prev); filteredDastEndpoints.forEach(e => n.add(epKey(e))); return n; });
  const deselectFilteredDast = () => setDastSelected(prev => { const n = new Set(prev); filteredDastEndpoints.forEach(e => n.delete(epKey(e))); return n; });

  const openDetail = async (e) => {
    if (!e.exchangeId) return;
    setDetailLoading(true); setDetailExchange(null);
    try {
      const ex = await httpExchangeService.getById(e.exchangeId);
      setDetailExchange(ex);
    } catch {
      setDetailExchange({ request: { method: e.method, url: e.url, headers: [] }, response: null });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTriggerScan = async () => {
    try {
      setScanning(true);
      setImportResult(null);
      const config = { scanProfile };
      if (selectedHosts.length > 0) config.hosts = selectedHosts;
      if (dastEnabled && dastProfileId) {
        const chosen = dastEndpoints.filter(e => dastSelected.has(epKey(e))).map(e => ({ method: e.method, url: e.url }));
        config.dast = { profileId: dastProfileId, endpoints: chosen };
      }
      const result = await exposiqService.triggerScan(webTarget._id, config);
      setScanStatus({ scanId: result.exposiqScanId, status: 'running', progress: 0, findingsCount: 0 });
      startPollingScanStatus(result.exposiqScanId);
    } catch (err) {
      setScanning(false);
      setScanStatus({ status: 'failed', error: err.response?.data?.error || err.message });
    }
  };

  const handleStopScan = async () => {
    if (!scanStatus?.scanId) return;
    try {
      setStopping(true);
      await exposiqService.stopScan(scanStatus.scanId);
      setScanStatus(prev => ({ ...prev, status: 'stopped' }));
      setScanning(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (err) {
      console.error('Failed to stop scan:', err);
    } finally {
      setStopping(false);
    }
  };

  const handleImportResults = async () => {
    if (!scanStatus?.scanId && !webTarget?.activeScan?.exposiqScanId) return;
    const scanId = scanStatus?.scanId || webTarget.activeScan.exposiqScanId;
    try {
      setImporting(true);
      const result = await exposiqService.importResults(webTarget._id, scanId);
      setImportResult(result);
      loadHistory();
      if (onRefresh) onRefresh();
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || err.message });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-auto h-full">
      {/* Connection Status Card */}
      <div className={`rounded-lg border p-4 ${cardBg}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faRobot} className="text-teal-500" />
            <h3 className={`font-semibold ${textPrimary}`}>ExposIQ Connection</h3>
          </div>
          <button
            onClick={checkConnection}
            className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-3">
          <FontAwesomeIcon
            icon={faCircle}
            className={`text-xs ${connectionStatus?.connected ? 'text-green-500' : 'text-red-500'}`}
          />
          <span className={textPrimary}>
            {connectionStatus?.connected ? 'Connected' : 'Not Connected'}
          </span>
          {connectionStatus?.version && (
            <span className={`text-xs ${textSecondary}`}>v{connectionStatus.version}</span>
          )}
          {connectionStatus?.oastEnabled && (
            <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">OAST</span>
          )}
        </div>
        {connectionStatus?.error && (
          <div className={`mt-2 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            {connectionStatus.error}
          </div>
        )}
        {!connectionStatus?.connected && (
          <p className={`mt-2 text-sm ${textSecondary}`}>
            <FontAwesomeIcon icon={faCog} className="mr-1" />
            Configure ExposIQ integration in Settings &gt; Integration Keys
          </p>
        )}
      </div>

      {/* Scan Trigger Card */}
      {connectionStatus?.connected && (
        <div className={`rounded-lg border p-4 ${cardBg}`}>
          <h3 className={`font-semibold mb-3 ${textPrimary}`}>Start Vulnerability Scan</h3>
          <div className="flex items-center gap-3 mb-4">
            <label className={`text-sm ${textSecondary}`}>Profile:</label>
            <select
              value={scanProfile}
              onChange={e => setScanProfile(e.target.value)}
              disabled={scanning}
              className={`px-3 py-1.5 rounded text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              {exposiqService.scanProfiles.map(p => (
                <option key={p.key} value={p.key}>{p.label} ({p.duration})</option>
              ))}
            </select>
          </div>

          {/* Hosts to scan (host-level scan, no specific URLs) */}
          <div className={`mb-4 rounded border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${textPrimary}`}>Hosts to scan</span>
              <span className={`text-xs ${textMuted}`}>{selectedHosts.length} selected</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {discoveredHosts.length === 0 && (
                <span className={`text-xs ${textMuted}`}>No hosts discovered yet — add one below.</span>
              )}
              {discoveredHosts.map(host => {
                const active = selectedHosts.includes(host);
                return (
                  <button
                    key={host}
                    type="button"
                    onClick={() => toggleHost(host)}
                    disabled={scanning}
                    className={`text-xs px-2 py-1 rounded border flex items-center gap-1.5 ${
                      active
                        ? 'bg-teal-600 border-teal-500 text-white'
                        : isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FontAwesomeIcon icon={active ? faCheckCircle : faCircle} className="text-[10px]" />
                    {host}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={customHost}
                onChange={e => setCustomHost(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomHost(); } }}
                placeholder="Add another host (e.g. api.example.com)"
                disabled={scanning}
                className={`flex-1 px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300'}`}
              />
              <button
                type="button"
                onClick={addCustomHost}
                disabled={scanning || !customHost.trim()}
                className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-40`}
              >
                Add
              </button>
            </div>
            <div className={`text-[11px] mt-1.5 ${textSecondary}`}>
              Scans one or several hosts (no specific URLs). Leave the target host selected for a standard scan, or add/deselect hosts as needed.
            </div>
          </div>

          {/* Authenticated DAST (Nuclei fuzzing of captured endpoints as a user) */}
          <div className={`mb-4 rounded border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={dastEnabled} onChange={e => setDastEnabled(e.target.checked)} disabled={scanning} />
              <span className={textPrimary}>Authenticated DAST — fuzz this profile's captured endpoints (XSS/SQLi/SSRF/RCE…) as the logged-in user</span>
            </label>
            {dastEnabled && (
              <div className="mt-2 flex items-center gap-2 pl-6">
                <span className={`text-xs ${textSecondary}`}>Session profile:</span>
                <ProfileSelect
                  profiles={authProfiles}
                  value={dastProfileId}
                  onChange={setDastProfileId}
                  darkMode={isDarkMode}
                />
                {dastAuthHeaders.length > 0 && (
                  <span className={`text-[11px] ${textMuted}`}>+ {dastAuthHeaders.join(', ')}</span>
                )}
              </div>
            )}

            {/* Endpoint selection: choose exactly which URLs get fuzzed */}
            {dastEnabled && dastProfileId && (
              <div className="mt-3 pl-6">
                {dastLoadingEndpoints ? (
                  <div className={`text-xs ${textSecondary}`}><FontAwesomeIcon icon={faSpinner} spin className="mr-1" /> Loading captured endpoints…</div>
                ) : dastEndpoints.length === 0 ? (
                  <div className={`text-xs ${textSecondary}`}>No non-static endpoints captured for this profile yet. Browse the target through the proxy as this user first.</div>
                ) : (
                  <div className={`rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    {/* Toolbar */}
                    <div className={`flex items-center gap-2 p-2 border-b flex-wrap ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="relative flex-1 min-w-[160px]">
                        <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${textMuted}`} />
                        <input
                          type="text"
                          value={dastSearch}
                          onChange={e => setDastSearch(e.target.value)}
                          placeholder="Filter URLs…"
                          className={`w-full pl-7 pr-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300'}`}
                        />
                      </div>
                      <select
                        value={dastMethodFilter}
                        onChange={e => setDastMethodFilter(e.target.value)}
                        className={`px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      >
                        {dastMethods.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={selectAllDast} className={`text-[11px] px-1.5 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
                        <button type="button" onClick={deselectAllDast} className={`text-[11px] px-1.5 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>None</button>
                        {(dastSearch || dastMethodFilter !== 'ALL') && (
                          <>
                            <button type="button" onClick={selectFilteredDast} className={`text-[11px] px-1.5 py-1 rounded ${isDarkMode ? 'bg-teal-800 text-teal-200 hover:bg-teal-700' : 'bg-teal-100 text-teal-700 hover:bg-teal-200'}`}>+Filtered</button>
                            <button type="button" onClick={deselectFilteredDast} className={`text-[11px] px-1.5 py-1 rounded ${isDarkMode ? 'bg-red-900/60 text-red-300 hover:bg-red-800' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>−Filtered</button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Count */}
                    <div className={`px-2 py-1 text-[11px] flex items-center justify-between ${textMuted} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <span><strong className={textPrimary}>{dastSelected.size}</strong> of {dastEndpoints.length} selected</span>
                      {filteredDastEndpoints.length !== dastEndpoints.length && <span>{filteredDastEndpoints.length} shown</span>}
                    </div>
                    {/* List */}
                    <div className="max-h-72 overflow-auto">
                      {filteredDastEndpoints.map((e) => {
                        const k = epKey(e);
                        const checked = dastSelected.has(k);
                        return (
                          <div
                            key={k}
                            className={`flex items-center gap-2 px-2 py-1 text-xs border-b last:border-b-0 ${isDarkMode ? 'border-gray-800 hover:bg-gray-800/60' : 'border-gray-100 hover:bg-gray-50'}`}
                          >
                            <input type="checkbox" checked={checked} onChange={() => toggleEndpoint(e)} className="cursor-pointer" />
                            <span className={`font-mono font-semibold w-14 shrink-0 ${METHOD_COLORS[e.method] || textSecondary}`}>{e.method}</span>
                            <button
                              type="button"
                              onClick={() => openDetail(e)}
                              title={e.url}
                              className={`flex-1 text-left truncate min-w-0 hover:underline ${e.exchangeId ? (isDarkMode ? 'text-gray-300' : 'text-gray-700') : textMuted} ${e.exchangeId ? 'cursor-pointer' : 'cursor-default'}`}
                            >
                              {e.url}
                            </button>
                            {e.hasBody && <span className={`text-[10px] px-1 rounded shrink-0 ${isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>body</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {dastEnabled && (
              <div className={`text-[11px] mt-1.5 pl-6 ${textSecondary}`}>
                Sends the profile's cookies/token + the <strong>selected</strong> requests (method, URL, body) to ExposIQ so Nuclei runs authenticated. Click a URL to inspect its request/response. Static assets are excluded.
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {(() => {
              const disabled = scanning || selectedHosts.length === 0 || (dastEnabled && (!dastProfileId || dastSelected.size === 0));
              return (
                <button
                  onClick={handleTriggerScan}
                  disabled={disabled}
                  className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                    disabled ? 'bg-gray-500 text-gray-300 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  <FontAwesomeIcon icon={scanning ? faSpinner : faPlay} spin={scanning} />
                  {scanning ? 'Scan Running...'
                    : dastEnabled ? `Start Authenticated DAST (${dastSelected.size} URL${dastSelected.size === 1 ? '' : 's'})`
                    : (selectedHosts.length > 1 ? `Start ExposIQ Scan (${selectedHosts.length} hosts)` : 'Start ExposIQ Scan')}
                </button>
              );
            })()}
            {scanning && (
              <button
                onClick={handleStopScan}
                disabled={stopping}
                className="px-4 py-2 rounded text-sm font-medium flex items-center gap-2 bg-red-600 text-white hover:bg-red-700"
              >
                <FontAwesomeIcon icon={stopping ? faSpinner : faStop} spin={stopping} />
                Stop
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scan Progress Card */}
      {scanStatus && (
        <div className={`rounded-lg border p-4 ${cardBg}`}>
          <h3 className={`font-semibold mb-3 ${textPrimary}`}>Scan Progress</h3>
          <div className="space-y-3">
            {/* Status + phase */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium px-2 py-1 rounded ${
                scanStatus.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                scanStatus.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                scanStatus.status === 'stopped' ? 'bg-yellow-500/20 text-yellow-400' :
                scanStatus.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {scanStatus.status?.toUpperCase()}
              </span>
              {scanStatus.phase && scanStatus.phase !== 'unknown' && (
                <span className={`text-sm ${textSecondary}`}>
                  Phase: {scanStatus.phase}
                </span>
              )}
              {scanStatus.enginesCompleted !== undefined && scanStatus.enginesTotal && (
                <span className={`text-xs ${textMuted}`}>
                  ({scanStatus.enginesCompleted}/{scanStatus.enginesTotal} engines)
                </span>
              )}
              {scanStatus.duration && (
                <span className={`text-xs ${textMuted}`}>{scanStatus.duration}</span>
              )}
            </div>

            {/* Progress bar */}
            {['running', 'queued'].includes(scanStatus.status) && (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className={textSecondary}>Progress</span>
                  <span className={textPrimary}>{scanStatus.progress || 0}%</span>
                </div>
                <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-teal-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${scanStatus.progress || 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Findings by severity */}
            {scanStatus.findingsBySeverity && Object.keys(scanStatus.findingsBySeverity).length > 0 ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-sm ${textSecondary}`}>Findings:</span>
                {Object.entries(scanStatus.findingsBySeverity).map(([sev, count]) => (
                  count > 0 && (
                    <span key={sev} className="flex items-center gap-1 text-xs">
                      <span className={`w-2 h-2 rounded-full ${SEVERITY_COLORS[sev] || 'bg-gray-400'}`} />
                      <span className={textPrimary}>{count}</span>
                      <span className={textMuted}>{sev}</span>
                    </span>
                  )
                ))}
              </div>
            ) : (
              <div className={`text-sm ${textSecondary}`}>
                Findings: <span className={textPrimary}>{scanStatus.findingsCount || 0}</span>
              </div>
            )}

            {/* Import button (when completed) */}
            {scanStatus.status === 'completed' && (scanStatus.findingsCount > 0 || Object.values(scanStatus.findingsBySeverity || {}).reduce((a, b) => a + b, 0) > 0) && (
              <button
                onClick={handleImportResults}
                disabled={importing}
                className="px-4 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={importing ? faSpinner : faDownload} spin={importing} />
                {importing ? 'Importing findings...' : 'Import Findings into PurpleHQ'}
              </button>
            )}

            {scanStatus.error && (
              <div className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                {scanStatus.error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className={`rounded-lg border p-4 ${cardBg}`}>
          {importResult.error ? (
            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{importResult.error}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-500">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span className="font-medium">
                  Imported {importResult.imported} findings
                  {importResult.skippedDuplicates > 0 && (
                    <span className={`text-xs ${textMuted}`}> ({importResult.skippedDuplicates} duplicates skipped)</span>
                  )}
                </span>
              </div>
              {importResult.findings && importResult.findings.length > 0 && (
                <div className="mt-2 space-y-1">
                  {importResult.findings.slice(0, 15).map(f => (
                    <div key={f.id} className={`text-sm flex items-center gap-2 ${textSecondary}`}>
                      <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_COLORS[f.severity] || 'bg-gray-400'}`} />
                      <span className="flex-1 truncate">{f.title}</span>
                      {f.wstgId && <span className={`text-xs ${textMuted}`}>[{f.wstgId}]</span>}
                    </div>
                  ))}
                  {importResult.findings.length > 15 && (
                    <div className={`text-xs ${textMuted}`}>
                      ...and {importResult.findings.length - 15} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <div className={`rounded-lg border ${cardBg}`}>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`w-full flex items-center justify-between p-4 text-left ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
          >
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faHistory} className={textSecondary} />
              <h3 className={`font-semibold ${textPrimary}`}>Scan History</h3>
              <span className={`text-xs ${textMuted}`}>({scanHistory.length})</span>
            </div>
            <FontAwesomeIcon icon={showHistory ? faChevronDown : faChevronRight} className={`text-xs ${textMuted}`} />
          </button>
          {showHistory && (
            <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {scanHistory.map(scan => (
                <div
                  key={scan._id}
                  className={`px-4 py-2 flex items-center justify-between text-sm border-b last:border-b-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faShieldAlt} className={`text-xs ${textMuted}`} />
                    <span className={textPrimary}>{scan.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      scan.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {scan.status}
                    </span>
                    <span className={`text-xs ${textMuted}`}>
                      {new Date(scan.createdAt).toLocaleDateString()} {new Date(scan.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No active scan info */}
      {!scanStatus && !scanning && connectionStatus?.connected && (
        <div className={`rounded-lg border p-6 text-center ${cardBg}`}>
          <FontAwesomeIcon icon={faRobot} className={`text-4xl mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className={textSecondary}>No active scan</p>
          <p className={`text-sm mt-1 ${textMuted}`}>
            Select a scan profile above, or right-click a hostname in the site map and choose "Scan with ExposIQ".
          </p>
        </div>
      )}

      {/* Request/Response detail modal (click a DAST endpoint) */}
      {(detailExchange || detailLoading) && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
          onClick={() => { setDetailExchange(null); setDetailLoading(false); }}
        >
          <div
            className={`w-full max-w-5xl h-[80vh] rounded-lg border flex flex-col overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-sm font-semibold ${textPrimary}`}>Request / Response</span>
              <button onClick={() => { setDetailExchange(null); setDetailLoading(false); }} className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden flex">
              {detailLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-teal-500" />
                </div>
              ) : (
                <ExchangePanel exchange={detailExchange} title="Exchange" titleColor={textPrimary} isDarkMode={isDarkMode} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExposIQScanTab;
